import React, { useState, useCallback, useEffect } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import PropTypes from "prop-types";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import { getCountryByCode } from "@utils/utils";
import AutoComplete from "./AutoComplete";

import "./AutoComplete.scss";

const AddressSearchWithMap = (props) => {
  const {
    t,
    name,
    language,
    onAddressSelect,
    defaultAddress,
    isFloat,
    title,
    position,
    setPosition,
    error,
    icon,
    disabled,
    countryId,
    resetMapAddress,
  } = props;

  const generalParams = useGetGeneralParameters();
  const { countryList } = generalParams;
  const [formData, setFormData] = useState({
    address: { value: "" },
  });
  const [mapLatLng, setMapLatLng] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [currentAddress, setCurrentAddress] = useState(defaultAddress);
  const [isAddressInputFocussed, setAddressInputFocus] = useState(
    !!defaultAddress
  );
  const [isAddressSuggestionsLoading, setAddressSuggestionsLoadingStatus] =
    useState(false);

  const ADDRESS_SEARCH_TIMEOUT = 450;

  const fetchAddressSuggestions = useCallback(
    (initialAddress, limit, lang, position, countryId) => {
      setAddressSuggestionsLoadingStatus(true);
      let url = `/general/searchAddress?lang=${lang}&limit=${limit}&search=${initialAddress}&countryId=${countryId}`;
      if (position?.lng) {
        url = `/general/searchAddressByCoordinates?lang=${lang}&limit=1&latitude=${position?.lat}&longitude=${position?.lng}`;
      }
      axios
        .get(url)
        .then((response) => {
          setAddressSuggestions(response.data.items);
          setAddressSuggestionsLoadingStatus(false);
          if (position?.lng) {
            // setMapLatLng(true);
            setMapLatLng(response.data.items[0].address.label);
          }
        })
        .catch((error) => {
          if (window.debugMode) {
            console.error(
              "Error: an error occurred while fetching places",
              error
            );
          }
          setAddressSuggestions([]);
          setAddressSuggestionsLoadingStatus(false);
        });
    },
    []
  );

  const getAddress = (selectedAddress) => {
    const address = `${selectedAddress.address.houseNumber ?? ""} ${
      selectedAddress.address.street ?? ""
    }`;
    const germanAddress = `${selectedAddress.address.street ?? ""} ${
      selectedAddress.address.houseNumber ?? ""
    }`;
    const { city, postalCode, countryName, countryCode } =
      selectedAddress.address;
    let formattedAddress = "";
    switch (countryCode) {
      case "POL":
      case "GBR":
        formattedAddress = `${
          address ? `${address},` : ""
        } ${city},${postalCode},${countryName}`;
        break;
      case "DEU":
        formattedAddress = `${
          germanAddress ? `${germanAddress},` : ""
        } ${postalCode},${city},${countryName}`;
        break;
      case "CHE":
      case "FRA":
        formattedAddress = `${
          address ? `${address},` : ""
        } ${postalCode},${city},${countryName}`;
        break;
      default:
        formattedAddress = `${
          address ? `${address},` : ""
        } ${city},${postalCode},${countryName}`;
    }

    return formattedAddress;
  };

  const getStreet = (selectedAddress) => {
    const address = `${selectedAddress.address.houseNumber ?? ""} ${
      selectedAddress.address.street ?? ""
    }`;
    const germanAddress = `${selectedAddress.address.street ?? ""} ${
      selectedAddress.address.houseNumber ?? ""
    }`;
    const { countryCode } = selectedAddress.address;
    let formattedAddress = "";

    switch (countryCode) {
      case "DEU":
        formattedAddress = `${germanAddress ? `${germanAddress},` : ""}`;
        break;
      default:
        formattedAddress = `${address ? `${address},` : ""}`;
    }

    return formattedAddress;
  };

  /**
   * Handle jobsite selection.
   * @param {} item
   */
  const handleSiteSelection = (item) => {
    const selectedSite = addressSuggestions.find(
      (jobsite) => jobsite.address.label === item
    );
    if (selectedSite) {
      const updateFields = {
        ...formData,
        address: {
          value: getAddress(selectedSite),
          error: "",
          required: true,
        },
        street: {
          value: getStreet(selectedSite),
          error: "",
          required: true,
        },
        postal: {
          value: selectedSite.address.postalCode,
          error: "",
          required: false,
        },
        city: {
          value: selectedSite.address.city,
          error: "",
          required: false,
        },
        latitude: {
          value: position?.lat ?? selectedSite.position.lat,
          error: "",
          required: false,
        },
        longitude: {
          value: position?.lng ?? selectedSite.position.lng,
          error: "",
          required: false,
        },
        countryId: {
          value: getCountryByCode(
            selectedSite.address.countryCode,
            countryList
          ),
          error: "",
          required: false,
        },
      };
      setFormData(updateFields);
      setCurrentAddress(item);
      setAddressInputFocus(false);
      onAddressSelect(updateFields);
    } else {
      const updateFields = {
        ...formData,
        address: {
          value: "",
          error: "",
          required: true,
        },
        street: {
          value: "",
          error: "",
          required: true,
        },
        postal: {
          value: "",
          error: "",
          required: false,
        },
        city: {
          value: "",
          error: "",
          required: false,
        },
        latitude: {
          value: "",
          error: "",
          required: false,
        },
        longitude: {
          value: "",
          error: "",
          required: false,
        },
        countryId: {
          value: null,
          error: "",
          required: false,
        },
        company_reg: {
          value: "",
          error: "",
          required: false,
        },
      };
      setFormData(updateFields);
      setCurrentAddress(item);
      onAddressSelect(updateFields);
    }
  };

  useEffect(() => {
    if (mapLatLng !== "") {
      handleSiteSelection(mapLatLng);
      if (position?.lng) setPosition([]);
    }
  }, [mapLatLng]);
  /**
   * Handle jobsite change.
   * @param {*} value
   */
  const handleSiteChange = (value) => {
    setCurrentAddress(value);
    setAddressInputFocus(true);
    if (!value) {
      setCurrentAddress("");
      handleSiteSelection("");
    }
  };

  useEffect(() => {
    const debouncedFn = setTimeout(() => {
      if (currentAddress && !mapLatLng) {
        fetchAddressSuggestions(
          currentAddress,
          10,
          language,
          position,
          countryId
        );
        // if (position?.lng) setPosition([]);
      }
      setMapLatLng("");
    }, ADDRESS_SEARCH_TIMEOUT);

    return () => {
      if (debouncedFn) {
        clearTimeout(debouncedFn);
      }
    };
  }, [currentAddress, language, fetchAddressSuggestions]);

  useEffect(() => {
    const debouncedFn = setTimeout(() => {
      if (currentAddress && position?.lat) {
        fetchAddressSuggestions(
          currentAddress,
          10,
          language,
          position,
          countryId
        );
        // if (position?.lng) setPosition([]);
      }
    }, ADDRESS_SEARCH_TIMEOUT);

    return () => {
      if (debouncedFn) {
        clearTimeout(debouncedFn);
      }
    };
  }, [position?.lat]);

  useEffect(() => {
    if (resetMapAddress) setCurrentAddress("");
  }, [resetMapAddress]);

  return (
    <>
      <AutoComplete
        id={name}
        name={name}
        containerClass={
          !isFloat ? "v3-inquiry-form__jobsite-input-wrapper" : ""
        }
        label={t(title)}
        icon={icon}
        labelClass={
          error
            ? "v3-inquiry-form__jobsite-label_float_error required"
            : "v3-inquiry-form__jobsite-label_float required"
        }
        inputClass={
          error
            ? "v3-inquiry-form__job-site-input_error v3-inquiry-form__job-site-input--filled"
            : "v3-inquiry-form__job-site-input"
        }
        error={error}
        disabled={disabled}
        menuClassName="v3-inquiry-form__menu"
        menuItemClassName="v3-inquiry-form__menu-item"
        suggestions={addressSuggestions.map((jobsite) => jobsite.address.label)}
        value={currentAddress}
        isLoading={isAddressSuggestionsLoading}
        onChange={handleSiteChange}
        onSelect={handleSiteSelection}
        onFocus={() => setAddressInputFocus(true)}
        onBlur={() => {
          if (!currentAddress) {
            setAddressInputFocus(false);
          }
          if (currentAddress && addressSuggestions.length === 1) {
            setCurrentAddress(addressSuggestions[0].address.label);
            handleSiteSelection(addressSuggestions[0].address.label);
          }
        }}
      />
    </>
  );
};

AddressSearchWithMap.propTypes = {
  name: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  defaultAddress: PropTypes.string,
  onAddressSelect: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  isFloat: PropTypes.bool,
  title: PropTypes.string,
  error: PropTypes.string,
  icon: PropTypes.string,
  disabled: PropTypes.bool,
  countryId: PropTypes.number,
  resetMapAddress: PropTypes.bool,
};

AddressSearchWithMap.defaultProps = {
  defaultAddress: "",
  error: "",
  isFloat: false,
  title: "CLIENT_PLATFORM_ONBOARDING_SIMPLIFIED_SITE_FORM_SITE_ADDRESS",
  icon: null,
  disabled: false,
  countryId: null,
  resetMapAddress: false,
};

export default withTranslation()(AddressSearchWithMap);
